import { formatDateSafe } from '@/utils/dateUtils';
import type { VacationRequest, VacationRequestStatus, VacationSegment } from '../models/vacation.types';
import type { VacationReceipt } from '../models/vacationReceipt.types';
import { getSegmentTitle, sortSegmentsByDate } from './vacationSegments';

export const RECEIPT_LINKABLE_STATUSES: readonly VacationRequestStatus[] = [
    'Scheduled',
    'OnVacation',
    'Completed',
];

export interface ReceiptRequestOption {
    id: string;
    requestId: string;
    segmentOrder: number | null;
    label: string;
    startDate: string;
    endDate: string;
    hasReceipt: boolean;
}

export interface ReceiptRequestOptionGroup {
    label: string;
    options: ReceiptRequestOption[];
}

function buildSegmentLabel(
    segment: VacationSegment,
    position: number,
    totalSegments: number
): string {
    const title = getSegmentTitle(position, totalSegments);
    return `${title} · ${formatDateSafe(segment.startDate)} a ${formatDateSafe(segment.endDate)} · ${segment.days} dias`;
}

export function buildReceiptRequestOptions(
    requests: VacationRequest[],
    receipts: VacationReceipt[] = []
): ReceiptRequestOptionGroup[] {
    const receiptsByRequest = new Map<string, Set<number | null>>();
    for (const receipt of receipts) {
        if (!receipt.requestId) continue;
        if (!receiptsByRequest.has(receipt.requestId)) {
            receiptsByRequest.set(receipt.requestId, new Set());
        }
        receiptsByRequest.get(receipt.requestId)!.add(receipt.segmentOrder);
    }

    const groups: ReceiptRequestOptionGroup[] = [];

    const linkableRequests = requests.filter((request) =>
        RECEIPT_LINKABLE_STATUSES.includes(request.status)
    );

    for (const request of linkableRequests) {
        const sorted = sortSegmentsByDate(request.segments);
        if (sorted.length === 0) continue;

        const hasReceipts = receiptsByRequest.get(request.id);

        if (sorted.length === 1) {
            const segment = sorted[0];
            const hasReceipt = hasReceipts?.has(null) ?? false;

            groups.push({
                label: '',
                options: [
                    {
                        id: request.id,
                        requestId: request.id,
                        segmentOrder: null,
                        label: `${formatDateSafe(segment.startDate)} a ${formatDateSafe(segment.endDate)} · ${segment.days} dias`,
                        startDate: segment.startDate,
                        endDate: segment.endDate,
                        hasReceipt,
                    },
                ],
            });
        } else {
            const startDate = sorted[0].startDate;
            const endDate = sorted[sorted.length - 1].endDate;
            const groupLabel = `${formatDateSafe(startDate)} a ${formatDateSafe(endDate)} · ${request.totalDays} dias (fracionada)`;

            groups.push({
                label: groupLabel,
                options: sorted.map((segment) => {
                    const hasReceipt = hasReceipts?.has(segment.order) ?? false;
                    return {
                        id: `${request.id}#${segment.order}`,
                        requestId: request.id,
                        segmentOrder: segment.order,
                        label: buildSegmentLabel(segment, segment.position, sorted.length),
                        startDate: segment.startDate,
                        endDate: segment.endDate,
                        hasReceipt,
                    };
                }),
            });
        }
    }

    return groups.sort((a, b) => {
        const aStart = a.options[0]?.startDate ?? '';
        const bStart = b.options[0]?.startDate ?? '';
        return bStart.localeCompare(aStart);
    });
}

export function buildReceiptsByRequestId(receipts: VacationReceipt[]): Map<string, VacationReceipt[]> {
    const map = new Map<string, VacationReceipt[]>();
    for (const receipt of receipts) {
        if (receipt.requestId) {
            if (!map.has(receipt.requestId)) {
                map.set(receipt.requestId, []);
            }
            map.get(receipt.requestId)!.push(receipt);
        }
    }
    return map;
}

export function resolveReceiptPeriodLabel(
    receipt: Pick<VacationReceipt, 'requestId' | 'segmentOrder'>,
    request?: Pick<VacationRequest, 'segments'> | null
): string | null {
    if (!receipt.requestId || receipt.segmentOrder === null || !request?.segments) {
        return null;
    }

    const segments = request.segments;
    if (segments.length <= 1) {
        return null;
    }

    const sorted = sortSegmentsByDate(segments);
    const position = sorted.findIndex((s) => s.order === receipt.segmentOrder) + 1;
    if (position === 0) return null;

    return getSegmentTitle(position, sorted.length);
}
